import * as React from 'react';
import { IItemCache } from '../../models/IItemCacheObserver';
import { connectToItemCache } from './connectToItemCache';
import { connectToItemsScope } from './connectToItemsScope';
import { IDetailsList, IDetailsListProps } from 'office-ui-fabric-react/lib/DetailsList';
import { startItemIndexInViewFacet } from '../../facets/ScopeFacets';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { resolveItems } from '../../operations/getItems';
import { IComponentDecorator, IDecoratedProps } from '../../utilities/components/Decorators';
import { IItemsScopeStateProps } from '../itemsScope/ItemsScope';

export interface IWithCachedScrollPositionProps {
  /**
   * The key for an item set. This is 'itemKey' for compatibility with other decorators.
   */
  itemKey: string | undefined;
}

export type IInjectedProps = Pick<IDetailsListProps, 'componentRef'>;

export interface IScrollPositionProps {
  cachedScrollPosition: number;
  saveScrollPosition?: (payload: { lastScrollPosition: number; }) => void;
}

export type IDecorateWithCachedScrollPositionProps<TProps> = IDecoratedProps<
  TProps,
  IInjectedProps,
  IWithCachedScrollPositionProps & IInjectedProps
  >;

export function withCachedScrollPosition(): IComponentDecorator<IInjectedProps, IWithCachedScrollPositionProps & IInjectedProps> {
  return <P extends IInjectedProps>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, IInjectedProps, IWithCachedScrollPositionProps & IInjectedProps>> => {
    const Component = component;

    type IScrollPositionManagerProps = IDecoratedProps<P, IInjectedProps, IScrollPositionProps & IInjectedProps>;

    interface IScrollPositionState {
      hasComponentReference: boolean;
      initialScrollPositionSet: boolean;
    }

    class ScrollPositionManager extends React.PureComponent<IScrollPositionManagerProps, IScrollPositionState> {
      private _component: IDetailsList | undefined;

      constructor(props: IScrollPositionManagerProps) {
        super(props);

        this.state = {
          hasComponentReference: false,
          initialScrollPositionSet: false
        };
      }

      public render(): JSX.Element | null {
        const {
          cachedScrollPosition,
          ...listProps
        } = this.props as IScrollPositionProps;
        return (
          <Component
            componentRef={ this._onComponentRef }
            { ...listProps }
          />
        );
      }

      public componentDidUpdate(prevProps: IScrollPositionManagerProps, prevState: IScrollPositionState): void {
        if (this._component && this.props.cachedScrollPosition !== 0 &&
          !prevState.initialScrollPositionSet && !this.state.initialScrollPositionSet) {
          this._component.scrollToIndex(this.props.cachedScrollPosition);
          this.setState({
            initialScrollPositionSet: true
          });
        }
      }

      public componentWillUnmount(): void {
        if (this._component) {
          // todo: change to this._component.getStartItemIndexInView() when fabric is integrated
          const lastScrollPosition = 0;

          const {
            saveScrollPosition
          } = this.props;

          if (saveScrollPosition) {
            saveScrollPosition({
              lastScrollPosition: lastScrollPosition
            });
          }
        }
      }

      // this is called after componentDidMount() so need to force an update
      // to scroll to the cached scroll index
      private _onComponentRef = (detailsList: IDetailsList | null): void => {
        this._component = detailsList || undefined;
        if (this._component && this.props.cachedScrollPosition !== 0) {
          this.setState({
            hasComponentReference: true
          });
        }
      }
    }

    function mapItemCacheToCacheManagerProp(
      itemCache: IItemCache,
      props: IWithCachedScrollPositionProps & IItemsScopeStateProps
    ): Pick<IScrollPositionManagerProps, 'cachedScrollPosition'> {
      const { itemKey, itemsScopeItemKey } = props;
      const lastScrollPosition = itemCache.demandItemFacet(startItemIndexInViewFacet, itemsScopeItemKey);
      return {
        cachedScrollPosition: itemKey && lastScrollPosition && lastScrollPosition[itemKey] || 0
      };
    }

    function dispatchCacheFunction(
      dispatch: IItemCacheDispatch,
      props: IWithCachedScrollPositionProps & IItemsScopeStateProps
    ): Pick<IScrollPositionManagerProps, 'saveScrollPosition'> {
      const {
        itemKey,
        itemsScopeItemKey
      } = props;
      return {
        saveScrollPosition: itemKey ? (payload: { lastScrollPosition: number; }) => {
          dispatch(resolveItems({
            items: {
              [itemsScopeItemKey]: {
                ...startItemIndexInViewFacet.pack({
                  [itemKey]: payload.lastScrollPosition
                })
              }
            }
          }));
        } : undefined
      };
    }

    const WithCachedScrollPosition = connectToItemCache(mapItemCacheToCacheManagerProp, dispatchCacheFunction)(ScrollPositionManager);

    // @ts-ignore Need exclude type from TypeScript 2.8.
    return connectToItemsScope()(
      WithCachedScrollPosition
    );
  };
}
